import axios from 'axios';
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaSearch, FaPlus, FaTrash, FaCheckCircle, FaTruck, FaTimesCircle, FaEye, FaEdit, FaArrowLeft, FaShare, FaWhatsapp, FaShoppingCart, FaUser, FaTasks } from 'react-icons/fa';
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function AdminOrderDetailsPage() {
    const [orderData, setOrderData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState('');
    const [modifyingItem, setModifyingItem] = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const [counts, setCounts] = useState({
        pendingOrdersCount: 0,
        confirmedOrdersCount: 0,
        shippedOrdersCount: 0,
        deliveredOrdersCount: 0,
        cancelledOrdersCount: 0
    });

    const { id } = useParams();
    const navigate = useNavigate();

    const handleBackButtonClick = () => {
        navigate(-1); // Go back one step in browser history
    };

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const fetchOrderDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const [orderRes, countsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/order-details/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE_URL}/counts`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setOrderData(orderRes.data);
            setCounts(countsRes.data);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/orders'), 2000);
        } finally {
            setLoading(false);
        }
    };

    // Debounced search effect
    const debounceTimeout = useRef();
    useEffect(() => {
        if (searchTerm.length > 2) {
            setSearchLoading(true);
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
            debounceTimeout.current = setTimeout(async () => {
                try {
                    const token = localStorage.getItem('authToken');
                    const response = await axios.get(`${API_BASE_URL}/product-list?search=${searchTerm}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setFilteredProducts(response.data || []);
                } catch (error) {
                    handleApiError(error);
                } finally {
                    setSearchLoading(false);
                }
            }, 400);
            return () => clearTimeout(debounceTimeout.current);
        } else {
            setFilteredProducts([]);
            setSearchLoading(false);
        }
    }, [searchTerm]);

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAddItem = async (product) => {
        if (!window.confirm(`Add "${product.name}" to this order?`)) return;

        setModifyingItem(product._id);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/add-item-to-order/${id}`, product, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Item added successfully!');
            await fetchOrderDetails();
            setSearchTerm('');
            setFilteredProducts([]);
            setShowSearch(false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setModifyingItem(null);
        }
    };

    const handleRemoveItem = async (item) => {
        if (!window.confirm(`Remove "${item.name}" from this order?`)) return;

        setModifyingItem(item._id);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/remove-item-from-order/${id}`, item, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Item removed successfully!');
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setModifyingItem(null);
        }
    };

    const handleUpdateStatus = async (newStatus, extraData = {}) => {
        if (newStatus !== 'accepted' && !window.confirm(`Change order status to "${newStatus.toUpperCase()}"?`)) return;

        setUpdatingStatus(newStatus);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-order-status/${id}`,
                { status: newStatus, ...extraData },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            alert(`Order status updated to ${newStatus.toUpperCase()}`);
            await fetchOrderDetails();
        } catch (error) {
            handleApiError(error);
        } finally {
            setUpdatingStatus('');
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'warning',
            accepted: 'primary',
            shipped: 'info',
            delivered: 'success',
            cancelled: 'danger'
        };
        return colors[status] || 'secondary';
    };

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
                    <LoadingScreen />
                    <p className="mt-3 text-muted small fw-semibold" style={{ letterSpacing: '0.5px' }}>Loading order details...</p>
                </div>
            </div>
        );
    }

    if (!orderData) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="text-center mt-5">
                    <h5 className="text-muted">Order not found</h5>
                    <button className="btn btn-danger mt-3" onClick={() => navigate('/admin/orders')}>
                        Back to Orders
                    </button>
                </div>
            </div>
        );
    }

    const orderStatusTiles = [
        { status: 'pending', title: 'PENDING ORDERS', count: counts.pendingOrdersCount, link: '/admin/pending-orders', icon: FaShoppingCart },
        { status: 'confirmed', title: 'CONFIRMED ORDERS', count: counts.confirmedOrdersCount, link: '/admin/confirmed-orders', icon: FaCheckCircle },
        { status: 'shipped', title: 'SHIPPED ORDERS', count: counts.shippedOrdersCount, link: '/admin/shipped-orders', icon: FaTruck },
        { status: 'delivered', title: 'DELIVERED ORDERS', count: counts.deliveredOrdersCount, link: '/admin/delivered-orders', icon: FaCheckCircle },
        { status: 'cancelled', title: 'CANCELLED ORDERS', count: counts.cancelledOrdersCount, link: '/admin/cancelled-orders', icon: FaTimesCircle }
    ];

    const totalOrders = orderStatusTiles.reduce((s, t) => s + (t.count || 0), 0);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row g-0 mb-3">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1 d-inline-flex align-items-center gap-3 flex-wrap">
                                    <span>ORDER DETAILS - {(orderData.token || orderData._id)?.toUpperCase()}</span>
                                    <span className={`badge bg-${getStatusColor(orderData.status)} fs-7 fw-semibold`} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', marginTop: '-5px', marginBottom: '-5px', letterSpacing: 'normal', textTransform: 'uppercase', lineHeight: '1' }}>
                                        {orderData.status || 'pending'}
                                    </span>
                                </h4>
                                <p className="text-muted p-0 m-0">Manage and modify order information</p>
                            </div>
                            <button
                                className="back-btn-pill"
                                onClick={handleBackButtonClick}
                                title="Go Back"
                            >
                                <FaArrowLeft />
                                Back
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-0 order-tiles-row mb-4">
                    {orderStatusTiles.map((item) => {
                        const Icon = item.icon;
                        const pct = totalOrders ? ((item.count / totalOrders) * 100).toFixed(1) : 0;
                        const isActive = item.status === orderData.status;
                        return (
                            <div key={item.status} className="col-12 col-sm-6 col-md-4 col-xl">
                                <Link to={item.link} className={`order-tile-card order-tile-${item.status} ${isActive ? 'active' : ''} text-decoration-none`}>
                                    <div className="order-tile-top d-flex justify-content-between align-items-start mb-2">
                                        <div className="order-tile-icon-wrapper"><Icon className="order-tile-icon" /></div>
                                        <span className="order-tile-count">{item.count}</span>
                                    </div>
                                    <h6 className="order-tile-title mb-1">{item.title}</h6>
                                    <div className="order-tile-progress mt-2" aria-label="Status share" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100">
                                        <div className="order-tile-progress-bar" style={{ width: pct + '%' }}></div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3 small">
                                        <span className="text-muted">{pct}%</span>
                                        <span className="order-tile-link-label">VIEW</span>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>

                <div className="row g-0" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
                    {/* Left Column: Customer & Location + Order Items */}
                    <div className="col-lg-8 col-md-12">
                        {/* Customer & Location Info Card */}
                        <div className="details-card-custom mb-0">
                            <div className="details-card-header">
                                <h5>
                                    <FaUser className="text-danger me-2" />
                                    Customer & Location
                                </h5>
                            </div>
                            <div className="details-info-row">
                                <div className="details-info-block">
                                    <span className="details-info-label">Customer Name</span>
                                    <strong className="details-info-value">{orderData.address?.name || 'N/A'}</strong>
                                </div>
                                <div className="details-info-block">
                                    <span className="details-info-label">Mobile Number</span>
                                    <strong className="details-info-value text-primary">{orderData.mobile || 'N/A'}</strong>
                                </div>
                                <div className="details-info-block">
                                    <span className="details-info-label">Order Date</span>
                                    <strong className="details-info-value">{formatDate(orderData.createdAt)}</strong>
                                </div>
                            </div>
                            <div className="details-address-block">
                                <span className="details-address-label">Delivery Address</span>
                                <span className="details-address-value">
                                    {orderData.address ? (
                                        <>
                                            {orderData.address.house}, {orderData.address.area}
                                            {orderData.address.landmark ? <><br /><small className="text-muted">Landmark: {orderData.address.landmark}</small></> : ''}
                                            {orderData.address.city ? <><br /><small className="text-muted">City: {orderData.address.city}</small></> : ''}
                                        </>
                                    ) : (
                                        'No address details available'
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Order Items & Price Summary Card */}
                        <div className="details-card-custom">
                            <div className="details-card-header">
                                <h5>
                                    <FaShoppingCart className="text-danger me-2" />
                                    Order Items ({orderData.items?.length || 0})
                                </h5>
                                <button
                                    className="btn btn-outline-danger btn-sm rounded-pill px-3 fw-semibold"
                                    style={{ fontSize: '0.8rem' }}
                                    onClick={() => setShowSearch(!showSearch)}
                                    disabled={['shipped', 'delivered', 'cancelled'].includes(orderData.status)}
                                >
                                    <FaPlus className="me-1" />
                                    Add Item
                                </button>
                            </div>
                            {showSearch && (
                                <div className="p-3 border-bottom">
                                    <div className="mb-0">
                                        <div className="search-control-wrapper">
                                            <input
                                                type="text"
                                                className="form-control search-input-custom w-100"
                                                placeholder="Search products to add..."
                                                value={searchTerm}
                                                onChange={handleSearch}
                                            />
                                            <FaSearch className="search-icon-custom" />
                                        </div>
                                        {searchLoading && (
                                            <div className="text-center pt-3">
                                                <span className="spinner-border spinner-border-sm text-danger" />
                                            </div>
                                        )}
                                        {filteredProducts.length > 0 && (
                                            <div className="mt-3">
                                                <small className="text-muted fw-bold d-block mb-1">Select a product to add:</small>
                                                <div className="list-group">
                                                    {filteredProducts.map(product => (
                                                        <button
                                                            key={product._id}
                                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                            onClick={() => handleAddItem(product)}
                                                            disabled={modifyingItem === product._id}
                                                        >
                                                            <div>
                                                                <strong>{product.name}</strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    Quantity: {product.quantity} • Price: {formatCurrency(product.price)}
                                                                </small>
                                                            </div>
                                                            {modifyingItem === product._id ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                <FaPlus className="text-danger" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {orderData.items?.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0 details-products-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th className="text-center">Quantity</th>
                                                <th className="text-end">Price</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderData.items.map((item, index) => (
                                                <tr key={`${item._id}-${index}`}>
                                                    <td>
                                                        <strong className="text-dark">{item.name}</strong>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="badge bg-danger px-2.5 py-1.5 fw-semibold">{item.quantity}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <strong className="text-dark">{formatCurrency(item.price)}</strong>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="order-action-btn order-action-btn-cancel mx-auto"
                                                            style={{ width: '28px', height: '28px', fontSize: '0.78rem' }}
                                                            onClick={() => handleRemoveItem(item)}
                                                            disabled={modifyingItem === item._id || ['shipped', 'delivered', 'cancelled'].includes(orderData.status)}
                                                            title="Remove Item"
                                                        >
                                                            {modifyingItem === item._id ? (
                                                                <span className="spinner-border spinner-border-sm text-danger" />
                                                            ) : (
                                                                <FaTrash />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted border-bottom">
                                    No items in this order
                                </div>
                            )}

                            {/* Price summary details box */}
                            <div className="p-3">
                                {(() => {
                                    const subtotal = orderData.items?.reduce((acc, item) => acc + (item.price * item.quantity), 0) || 0;
                                    let discount = 0;
                                    if (
                                        orderData.appliedOffer &&
                                        orderData.appliedOffer._id &&
                                        subtotal >= (orderData.appliedOffer.minAmount || 0)
                                    ) {
                                        if (orderData.appliedOffer.unit === 'Percent') {
                                            discount = Math.round(subtotal * orderData.appliedOffer.value / 100);
                                        } else if (orderData.appliedOffer.unit === 'Rupees') {
                                            discount = orderData.appliedOffer.value;
                                        }
                                        if (discount > subtotal) discount = subtotal;
                                    }
                                    const delivery = (orderData.deliveryCharge && !isNaN(Number(orderData.deliveryCharge))) ? Number(orderData.deliveryCharge) : 0;
                                    const grandTotal = (subtotal - discount) + delivery;

                                    return (
                                        <div className="invoice-summary-box mt-0">
                                            <div className="invoice-summary-row">
                                                <span className="invoice-label">Subtotal:</span>
                                                <span className="invoice-value">{formatCurrency(subtotal)}</span>
                                            </div>
                                            {orderData.appliedOffer && (
                                                <div className="invoice-summary-row">
                                                    <span className="invoice-label">Offer Discount ({orderData.appliedOffer.name}):</span>
                                                    <span className="invoice-value text-danger">- {formatCurrency(discount)}</span>
                                                </div>
                                            )}
                                            <div className="invoice-summary-row">
                                                <span className="invoice-label">Delivery Charge:</span>
                                                <span className="invoice-value">
                                                    {orderData.deliveryCharge === 'To be updated' ? 'To be updated' : formatCurrency(delivery)}
                                                </span>
                                            </div>
                                            <div className="invoice-summary-row">
                                                <span className="invoice-label fs-6">Grand Total:</span>
                                                <span className="invoice-value grand-total">
                                                    {orderData.deliveryCharge === 'To be updated' ? 'To be updated' : formatCurrency(grandTotal)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Actions */}
                    <div className="col-lg-4 col-md-12">
                        <div className="details-card-custom">
                            <div className="details-card-header">
                                <h5>
                                    <FaTasks className="text-danger me-2" />
                                    Order Actions
                                </h5>
                                    <span className={`badge bg-${getStatusColor(orderData.status)} fs-7 fw-semibold`} style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 12px', letterSpacing: 'normal', textTransform: 'uppercase', lineHeight: '1' }}>
                                    {orderData.status?.toUpperCase() || 'PENDING'}
                                </span>
                            </div>
                            <div className="details-card-body p-3">
                                <div className="vertical-segmented-group">
                                    {orderData.status === 'pending' && (
                                        <button
                                            className="vertical-segmented-btn btn-accept"
                                            onClick={() => {
                                                const chargeInput = window.prompt("Enter delivery charge (₹) for this order (enter 0 for free delivery):", "0");
                                                if (chargeInput === null) return;
                                                const deliveryCharge = chargeInput.trim() !== "" ? chargeInput.trim() : "0";
                                                handleUpdateStatus('accepted', { deliveryCharge });
                                            }}
                                            disabled={updatingStatus === 'accepted'}
                                        >
                                            {updatingStatus === 'accepted' ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FaCheckCircle className="text-success" />
                                            )}
                                            Accept Order
                                        </button>
                                    )}

                                    {orderData.status === 'accepted' && (
                                        <button
                                            className="vertical-segmented-btn btn-ship"
                                            onClick={() => handleUpdateStatus('shipped')}
                                            disabled={updatingStatus === 'shipped'}
                                        >
                                            {updatingStatus === 'shipped' ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FaTruck className="text-info" />
                                            )}
                                            Mark as Shipped
                                        </button>
                                    )}

                                    {orderData.status === 'shipped' && (
                                        <button
                                            className="vertical-segmented-btn btn-deliver"
                                            onClick={() => handleUpdateStatus('delivered')}
                                            disabled={updatingStatus === 'delivered'}
                                        >
                                            {updatingStatus === 'delivered' ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FaCheckCircle className="text-success" />
                                            )}
                                            Mark as Delivered
                                        </button>
                                    )}

                                    {orderData.status !== 'cancelled' && orderData.status !== 'delivered' && (
                                        <button
                                            className="vertical-segmented-btn btn-cancel"
                                            onClick={() => handleUpdateStatus('cancelled')}
                                            disabled={updatingStatus === 'cancelled'}
                                        >
                                            {updatingStatus === 'cancelled' ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <FaTimesCircle className="text-danger" />
                                            )}
                                            Cancel Order
                                        </button>
                                    )}

                                    {orderData.address?.latitude && orderData.address?.longitude && (
                                        <button
                                            className="vertical-segmented-btn btn-location"
                                            onClick={() => {
                                                const url = `https://www.google.com/maps/search/?api=1&query=${orderData.address.latitude},${orderData.address.longitude}`;
                                                window.open(url, '_blank');
                                            }}
                                        >
                                            View Location in Maps
                                        </button>
                                    )}

                                    <button
                                        className="vertical-segmented-btn btn-whatsapp"
                                        onClick={() => {
                                            const url = orderData.address?.latitude && orderData.address?.longitude
                                                ? `https://www.google.com/maps/search/?api=1&query=${orderData.address.latitude},${orderData.address.longitude}`
                                                : '';
                                            const itemsList = orderData.items?.map(item => `• ${item.name} (Qty: ${item.quantity})`).join('\n') || 'No items';
                                            const details = `🚚 DELIVERY ORDER #${orderData.token || orderData._id || 'N/A'}\n\n📅 Order Date: ${formatDate(orderData.createdAt)}\n💰 Grand Total: ${formatCurrency(orderData.grandTotal !== undefined && orderData.grandTotal !== null ? orderData.grandTotal : (orderData.totalAmount || 0))}\n📦 Status: ${orderData.status?.toUpperCase() || 'PENDING'}\n\n👤 CUSTOMER DETAILS:\nName: ${orderData.address?.name || 'N/A'}\nPhone: ${orderData.mobile}\n\n📍 DELIVERY ADDRESS:\n${orderData.address?.house || ''}, ${orderData.address?.area || ''}\n${orderData.address?.landmark ? `Landmark: ${orderData.address.landmark}` : ''}\nCity: ${orderData.address?.city || 'Bareilly'}\n\n${url ? `🗺️ LOCATION (Click to open in Google Maps):\n${url}\n\n` : ''}📦 ITEMS TO DELIVER:\n${itemsList}\n\n⚠️ IMPORTANT: Please call customer before delivery.`;
                                            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(details)}`;
                                            window.open(whatsappUrl, '_blank');
                                        }}
                                    >
                                        <FaWhatsapp className="text-success" />
                                        Share on WhatsApp
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default AdminOrderDetailsPage;
